import React, { useEffect, useRef, useMemo } from 'react';

const ParticleBackground = ({ config = {} }) => {
  const canvasRef = useRef(null);
  
  // Default configuration
  const defaultConfig = {
    particleCount: 50,
    colors: ['#6366f1', '#8b5cf6', '#a78bfa'],
    speed: 0.5,
    size: 1.5,
    maxSize: 3,
    connectParticles: true,
    connectDistance: 150,
    interactivity: true,
  };
  
  // Merge default config with provided config - memoized to prevent recreation
  const finalConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    
    // Set canvas size
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    // Initialize particles
    const initParticles = () => {
      particles = [];
      for (let i = 0; i < finalConfig.particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * (finalConfig.maxSize - finalConfig.size) + finalConfig.size,
          color: finalConfig.colors[Math.floor(Math.random() * finalConfig.colors.length)],
          speedX: (Math.random() - 0.5) * finalConfig.speed,
          speedY: (Math.random() - 0.5) * finalConfig.speed,
        });
      }
    };
    
    // Draw particles
    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw particles
      particles.forEach((particle, i) => {
        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.speedX *= -1;
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.speedY *= -1;
        }
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.globalAlpha = 0.7;
        ctx.fill();
        
        // Connect particles
        if (finalConfig.connectParticles) {
          for (let j = i + 1; j < particles.length; j++) {
            const particle2 = particles[j];
            const distance = Math.sqrt(
              Math.pow(particle.x - particle2.x, 2) + 
              Math.pow(particle.y - particle2.y, 2)
            );
            
            if (distance < finalConfig.connectDistance) {
              ctx.beginPath();
              ctx.strokeStyle = particle.color;
              ctx.globalAlpha = 0.2 * (1 - distance / finalConfig.connectDistance);
              ctx.lineWidth = 0.5;
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(particle2.x, particle2.y);
              ctx.stroke();
            }
          }
        }
      });
      
      animationFrameId = requestAnimationFrame(drawParticles);
    };
    
    // Handle mouse interaction
    let mouse = {
      x: null,
      y: null,
      radius: 150
    };
    
    const handleMouseMove = (e) => {
      if (!finalConfig.interactivity) return;
      
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      
      // Affect nearby particles
      particles.forEach(particle => {
        const distance = Math.sqrt(
          Math.pow(particle.x - mouse.x, 2) + 
          Math.pow(particle.y - mouse.y, 2)
        );
        
        if (distance < mouse.radius) {
          const force = mouse.radius / distance;
          const angle = Math.atan2(particle.y - mouse.y, particle.x - mouse.x);
          const acceleration = force * 0.02;
          
          particle.speedX += Math.cos(angle) * acceleration;
          particle.speedY += Math.sin(angle) * acceleration;
          
          // Limit speed
          const maxSpeed = 2;
          const speed = Math.sqrt(particle.speedX * particle.speedX + particle.speedY * particle.speedY);
          if (speed > maxSpeed) {
            particle.speedX = (particle.speedX / speed) * maxSpeed;
            particle.speedY = (particle.speedY / speed) * maxSpeed;
          }
        }
      });
    };
    
    // Initialize
    setCanvasSize();
    initParticles();
    drawParticles();
    
    // Event listeners
    window.addEventListener('resize', () => {
      setCanvasSize();
      initParticles();
    });
    
    if (finalConfig.interactivity) {
      window.addEventListener('mousemove', handleMouseMove);
    }
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', setCanvasSize);
      if (finalConfig.interactivity) {
        window.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, [finalConfig]);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 bg-transparent"
      style={{ pointerEvents: 'none' }}
    />
  );
};

export default ParticleBackground;
